import React from 'react';
import { Link } from 'react-router-dom';
import { FadeIn } from '../components/FadeIn';
const domains = [{
  title: 'Consequential Thinking',
  desc: 'The ability to understand second-order and long-range outcomes rather than reacting only to immediate emotion or surface incentives.'
}, {
  title: 'Emotional Regulation',
  desc: 'The capacity to remain stable under stress, resist impulsive reaction, and preserve judgment during conflict, pressure, or provocation.'
}, {
  title: 'Reality Testing',
  desc: 'The ability to distinguish fact from distortion, resist delusion, and remain grounded in evidence rather than narrative capture.'
}, {
  title: 'Manipulation Resistance',
  desc: 'The capacity to detect propaganda, rhetorical coercion, tribal emotional triggers, and psychologically engineered influence.'
}, {
  title: 'Executive Discipline',
  desc: 'The ability to act with consistency, self-command, and responsibility across time rather than drifting through impulse or instability.'
}, {
  title: 'Long-Term Planning',
  desc: 'The capacity to think in strategic horizons, preserve continuity, and make decisions that strengthen future resilience.'
}, {
  title: 'Social Cooperation',
  desc: 'The ability to function within institutions, coordinate with others, and pursue stable outcomes without unnecessary factional breakdown.'
}, {
  title: 'Leadership Responsibility',
  desc: 'The capacity to exercise power with seriousness, burden-awareness, and constitutional restraint.'
}];
const sections = [{
  number: '01',
  heading: 'What is the NCJI?',
  paragraphs: ['The NCJI is not simply a test. It is a constitutional framework for understanding civic competence as something that can be measured, developed, and renewed over time. It exists because modern societies increasingly depend on the quality of judgment exercised by citizens, officials, and institutions operating under pressure, complexity, and information distortion.', 'Rather than treating all forms of decision-making as equally reliable, the NCJI distinguishes between reactive, captured, unstable judgment and disciplined, reality-based, consequential judgment. Its purpose is to align civic trust with demonstrated capacity while preserving pathways for individual development.']
}, {
  number: '02',
  heading: 'Core Domains',
  paragraphs: ['The NCJI is built around several core dimensions of judgment that shape civic reliability and leadership fitness.'],
  domainsList: true
}, {
  number: '03',
  heading: 'Why It Matters',
  paragraphs: ['Cognarchism treats judgment as a public good because poor judgment does not remain private for long. When emotionally unstable, manipulative, impulsive, or reality-detached decision-making enters public institutions, the consequences spread across law, finance, education, security, and civic trust.', 'The NCJI exists to reduce that risk. It gives the constitutional order a way to identify strengths, vulnerabilities, and developmental needs before those weaknesses become systemic failures. In that sense, it is both a civic framework and a defensive architecture against institutional decline.']
}, {
  number: '04',
  heading: 'Development, Not Only Selection',
  paragraphs: ['The NCJI is not meant to be a fixed hierarchy of permanent human worth. Its purpose is developmental. Citizens who score lower in specific domains are not simply excluded. They are directed toward training, recalibration, and structured improvement pathways.', 'This is one of the defining differences of Cognarchism. It does not merely sort people; it seeks to raise them. It assumes that many forms of poor judgment can be improved through discipline, education, feedback, and time. Civic competence is therefore treated not only as a threshold, but as a trajectory.']
}, {
  number: '05',
  heading: 'Role in Public Life',
  paragraphs: ['Under Cognarchism, the NCJI informs how civic trust is distributed across the political order. At the broadest level, it supports developmental citizenship by helping citizens understand where they are strong, where they are vulnerable, and how they can improve.', 'At higher levels, it supports role qualification. Offices with greater public consequence may require stronger demonstrated capacity in the domains most relevant to those responsibilities. The burden of proof rises with the consequence of power. In this way, the NCJI helps create a constitutional order in which authority is earned, not theatrically performed into existence.']
}, {
  number: '06',
  heading: 'Constitutional Limits',
  paragraphs: ['Because the NCJI sits close to the center of the political order, Cognarchism places constitutional safeguards around its use. It cannot lawfully function as a tool of arbitrary domination, factional punishment, or opaque exclusion. Its standards must be reviewable, its procedures governed by law, and its outcomes subject to appeal and reassessment.', 'The legitimacy of the NCJI depends on this balance: it must be serious enough to matter, but constrained enough to remain constitutional.']
}];
export function NCJI() {
  return <article data-id="element-336">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-cognarch-white border-b border-cognarch-border" data-id="element-337">
        <div className="max-w-3xl mx-auto px-6" data-id="element-338">
          <FadeIn delay={0.1} direction="down" data-id="element-339">
            <p className="uppercase tracking-[0.25em] text-xs font-medium text-cognarch-gray mb-8" data-id="element-340">
              Core Framework
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-341">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-medium leading-[1.1] tracking-tight text-cognarch-black mb-8 text-balance" data-id="element-342">
              The system for measuring and developing civic judgment
            </h1>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-343">
            <p className="text-lg md:text-xl text-cognarch-gray leading-relaxed mb-12 max-w-2xl" data-id="element-344">
              The Neurocognitive Civic Judgment Index, or NCJI, is the central
              evaluative and developmental framework of Cognarchism. It is
              designed to identify, strengthen, and maintain the forms of
              judgment most necessary for responsible citizenship, institutional
              trust, and high-consequence leadership.
            </p>
          </FadeIn>

          <FadeIn delay={0.5} className="flex flex-col sm:flex-row gap-4" data-id="element-345">
            <Link to="/charter" className="inline-block px-7 py-3.5 bg-cognarch-black text-white font-medium tracking-wide hover:bg-cognarch-charcoal transition-all duration-300 text-sm text-center" data-id="element-346">
              Read the Framework
            </Link>
            <Link to="/principles" className="inline-block px-7 py-3.5 border border-cognarch-charcoal text-cognarch-charcoal font-medium tracking-wide hover:bg-cognarch-charcoal hover:text-white transition-all duration-300 text-sm text-center" data-id="element-347">
              View Constitutional Principles
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Body Sections */}
      {sections.map((section, index) => <section key={section.number} className={`py-20 md:py-28 border-b border-cognarch-border ${index % 2 === 1 ? 'bg-cognarch-offwhite' : 'bg-cognarch-white'}`} data-id="element-348">
          <div className="max-w-3xl mx-auto px-6" data-id="element-349">
            <FadeIn data-id="element-350">
              <div className="flex items-baseline gap-6 mb-8" data-id="element-351">
                <span className="text-cognarch-border font-serif text-4xl md:text-5xl leading-none select-none" data-id="element-352">
                  {section.number}
                </span>
                <h2 className="text-2xl md:text-4xl font-serif text-cognarch-charcoal leading-tight" data-id="element-353">
                  {section.heading}
                </h2>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-354">
              <div className="space-y-6" data-id="element-355">
                {section.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-356">
                    {paragraph}
                  </p>)}

                {section.domainsList && <div className="space-y-8 py-4 border-t border-cognarch-border mt-8 pt-8" data-id="element-357">
                    {domains.map((domain, dIndex) => <div key={dIndex} className="flex gap-4" data-id="element-358">
                        <span className="text-cognarch-charcoal/30 text-xs mt-1.5 shrink-0" data-id="element-359">
                          ◆
                        </span>
                        <div data-id="element-360">
                          <h3 className="text-cognarch-charcoal font-serif text-lg md:text-xl mb-2" data-id="element-361">
                            {domain.title}
                          </h3>
                          <p className="text-cognarch-gray leading-[1.8] text-base" data-id="element-362">
                            {domain.desc}
                          </p>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </FadeIn>
          </div>
        </section>)}

      {/* Closing Statement Block */}
      <section className="py-24 md:py-32 bg-cognarch-black text-white" data-id="element-363">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-364">
          <FadeIn data-id="element-365">
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-366" />
            <p className="text-xl md:text-2xl font-serif leading-relaxed text-white/80 mb-12 text-balance" data-id="element-367">
              The NCJI is the core mechanism through which Cognarchism
              transforms judgment from an abstract virtue into a constitutional
              standard, a developmental pathway, and a public safeguard.
            </p>
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-368" />
            <p className="uppercase tracking-[0.25em] text-sm text-white/50 font-medium" data-id="element-369">
              Judgment measured. Capacity developed. Authority earned.
            </p>
          </FadeIn>
        </div>
      </section>
    </article>;
}